import { z } from 'zod';
import { OperationIdSchema, PlayerIdSchema } from '../../ids';
import { CharacterNameSchema, PlayerSchema } from '../../models/player';

/** An origin's id (CHAR-ORIGIN): one of the config's `origins` (format only). */
export const OriginIdSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]*$/)
  .max(32);
import { API_PREFIX, defineEndpoint } from '../endpoint';

/**
 * The characters of the signed-in account, oldest first, how many it may have, and the origins a
 * new one may come from, in order (CHAR-ORIGIN; their words are the catalogs' `origins.<id>`).
 */
export const listCharacters = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/characters`,
  access: 'account',
  response: z.object({
    characters: z.array(PlayerSchema),
    limit: z.number().int().positive(),
    origins: z.array(OriginIdSchema),
    /** The server's game time now: a character's `deleteAt` is read against it (CHAR-DELETE). */
    serverTime: z.number().int().nonnegative().optional(),
    /**
     * Whether a new character may skip its origin's memory (B165, PQ-6): once one of the account's
     * characters ended a prologue, deleted since or not.
     */
    canSkipMemory: z.boolean(),
  }),
});

/**
 * Creates a character, coming from `origin` (CHAR-ORIGIN, OI-1..5; Orven's when absent): where it
 * arrives and what it starts with; an origin with a memory lives it first (B165), unless `skipMemory`
 * and its account ended a prologue before (PQ-6; `memory_not_skippable` otherwise). Retrying with a
 * name the account already gave a character returns that character as it was made (safe retries
 * without an operation id).
 */
export const createCharacter = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/characters`,
  access: 'account',
  successStatus: 201,
  body: z.object({
    name: CharacterNameSchema,
    origin: OriginIdSchema.optional(),
    skipMemory: z.boolean().optional(),
  }),
  response: z.object({ character: PlayerSchema }),
});

/**
 * Starts deleting one of the account's characters (CHAR-DELETE, CD-1..8): it cannot be played from
 * now on, leaves its company and withdraws its offers and contracts (CD-5, CD-6), and is deleted
 * when its grace ends unless cancelled before. Idempotent by operation id.
 */
export const deleteCharacter = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/characters/:playerId/delete`,
  access: 'account',
  params: z.object({ playerId: PlayerIdSchema }),
  body: z.object({ operationId: OperationIdSchema }),
  response: z.object({ character: PlayerSchema }),
});

/** Cancels a character's deletion within its grace (CD-1): it can be played again. */
export const cancelDeleteCharacter = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/characters/:playerId/delete/cancel`,
  access: 'account',
  params: z.object({ playerId: PlayerIdSchema }),
  response: z.object({ character: PlayerSchema }),
});
